import { useEffect } from "react";
import { LoginScreen } from "./screens/auth/LoginScreen";
import { UpdatedTermsScreen } from "./screens/auth/UpdatedTermsScreen";
import { SignupScreen } from "./screens/auth/SignupScreen";
import { ForgotPasswordScreen } from "./screens/auth/ForgotPasswordScreen";
import { ResetPasswordScreen } from "./screens/auth/ResetPasswordScreen";
import { AcceptInviteScreen } from "./screens/auth/AcceptInviteScreen";
import { AdminPage } from "./screens/admin/AdminPage";
import { VerifyEmailScreen } from "./screens/auth/VerifyEmailScreen";
import { AnnotationScreen } from "./screens/annotate/AnnotationScreen";
import { Dashboard } from "./screens/dashboard/Dashboard";
import { AutoScreen } from "./screens/auto/AutoScreen";
import { TrainScreen } from "./screens/train/TrainScreen";
import { AugmentScreen } from "./screens/augment/AugmentScreen";
import { ModelsScreen } from "./screens/models/ModelsScreen";
import { PUBLIC_ROUTES, navigate, useRoute } from "./router";
import { SessionProvider, useSession } from "./state/session";
import { StatusProvider } from "./state/status";
import { DialogProvider } from "./ui/dialogs";
import { Shell } from "./ui/Shell";

function Routes() {
  const { status, user } = useSession();
  const route = useRoute();
  const authed = status === "authenticated";

  // Signed-out visitors only see the public pages (sign-in, sign-up, email links);
  // signed-in users never see sign-in or sign-up.
  useEffect(() => {
    if (status === "loading") return;
    if (!authed && !PUBLIC_ROUTES.has(route.name)) navigate({ name: "login" });
    if (authed && (route.name === "login" || route.name === "signup")) navigate({ name: "dashboard" });
  }, [status, authed, route.name]);

  if (status === "loading") return <div className="auth-page" data-testid="loading" />;
  // The email links open whether or not someone is signed in on this browser.
  if (route.name === "accept-invite") return <AcceptInviteScreen token={route.token} />;
  if (route.name === "verify-email") return <VerifyEmailScreen token={route.token} />;
  if (route.name === "reset-password") return <ResetPasswordScreen token={route.token} />;
  if (route.name === "forgot-password" && !authed) return <ForgotPasswordScreen />;
  if (!authed) return route.name === "signup" ? <SignupScreen /> : <LoginScreen />;
  if (user?.terms_outdated) return <UpdatedTermsScreen />;

  return (
    <Shell route={route}>
      {route.name === "dashboard" && <Dashboard />}
      {route.name === "admin" && <AdminPage />}
      {route.name === "annotate" && <AnnotationScreen key={route.projectId} projectId={route.projectId} imageId={route.imageId} />}
      {route.name === "train" && <TrainScreen key={route.projectId} projectId={route.projectId} />}
      {route.name === "augment" && <AugmentScreen key={`${route.projectId}-${route.classId ?? ""}`} projectId={route.projectId} classId={route.classId} />}
      {route.name === "models" && <ModelsScreen key={route.projectId} projectId={route.projectId} />}
      {route.name === "auto" && <AutoScreen key={`${route.projectId}-${route.version ?? ""}`} projectId={route.projectId} version={route.version} />}
    </Shell>
  );
}

export function App() {
  return (
    <SessionProvider>
      <StatusProvider>
        <DialogProvider>
          <Routes />
        </DialogProvider>
      </StatusProvider>
    </SessionProvider>
  );
}
